import React, { useState, useEffect } from 'react';
import './Team.css';

function Team({ userId, uplineId, userData, contract, fetchLevelUsers, addNotification }) {
  const [levelCounts, setLevelCounts] = useState([]);
  const [levelVolumes, setLevelVolumes] = useState([]);
  const [levelEligibility, setLevelEligibility] = useState([]);
  const [directReferrals, setDirectReferrals] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [levelUsers, setLevelUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const levelPercentages = [5, 3, 2, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1];

  useEffect(() => {
    if (contract && userId > 0) {
      fetchTeamData();
    }
  }, [contract, userId]);

  const fetchTeamData = async () => {
    if (!contract || userId <= 0) return;
    try {
      const [counts, volumes, eligibility, referrals] = await Promise.all([
        contract.getUserLevelCounts(userId),
        contract.getUserLevelVolumes(userId),
        contract.getUserLevelEligibility(userId),
        contract.getDirectReferrals(userId)
      ]);
      setLevelCounts(counts.map(c => c.toNumber()));
      setLevelVolumes(volumes.map(v => parseFloat(v.toString()) / 1e18));
      setLevelEligibility(eligibility);
      setDirectReferrals(referrals.map(r => r.toNumber()));
    } catch (error) {
      console.error('Failed to fetch team data:', error);
    }
  };

  const handleLevelClick = async (level) => {
    if (selectedLevel === level) {
      setSelectedLevel(null);
      setLevelUsers([]);
      return;
    }
    
    setSelectedLevel(level);
    setLoadingUsers(true);
    try {
      const users = await fetchLevelUsers(level);
      setLevelUsers(users);
    } catch (error) {
      addNotification('error', 'Failed to load level users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const copyReferralLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}?ref=${userId}`);
    addNotification('success', 'Referral link copied!');
  };

  return (
    <div className="team-page">
      <div className="team-header">
        <img src="/4.gif" alt="Team" className="header-gif" />
        <h1 className="page-title">Your Network</h1>
      </div>

      {/* Upline Info */}
      {uplineId > 0 && (
        <div className="upline-card animate-fade-in-up">
          <div className="upline-label">Your Upline</div>
          <div className="upline-id">ID: {uplineId}</div>
        </div>
      )}

      {/* Referral Card */}
      <div className="referral-card animate-fade-in-up">
        <div className="ref-header">
          <span className="ref-label">Your Referral ID</span>
          <div className="ref-id">{userId}</div>
        </div>
        <button className="copy-link-btn" onClick={copyReferralLink}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
          </svg>
          Copy Referral Link
        </button>
      </div>

      {/* Stats Grid */}
      <div className="team-stats-grid">
        <div className="team-stat-card">
          <div className="tsc-icon cyan">👥</div>
          <div className="tsc-value">{userData?.directReferrals || 0}</div>
          <div className="tsc-label">Direct Referrals</div>
        </div>
        <div className="team-stat-card">
          <div className="tsc-icon purple">💰</div>
          <div className="tsc-value">${parseFloat(userData?.directBusiness || 0).toFixed(0)}</div>
          <div className="tsc-label">Direct Business</div>
        </div>
        <div className="team-stat-card">
          <div className="tsc-icon green">📊</div>
          <div className="tsc-value">${parseFloat(userData?.teamVolume || 0).toFixed(0)}</div>
          <div className="tsc-label">Team Volume</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="team-tabs">
        <button className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>Overview</button>
        <button className={`tab-btn ${activeTab === 'levels' ? 'active' : ''}`} onClick={() => setActiveTab('levels')}>15 Levels</button>
        <button className={`tab-btn ${activeTab === 'direct' ? 'active' : ''}`} onClick={() => setActiveTab('direct')}>Direct</button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'overview' && (
          <div className="overview-content">
            <div className="overview-card">
              <h3>Level Income Structure</h3>
              <div className="income-tiers">
                <div className="tier-item"><span>L1</span><span className="tier-pct">5%</span></div>
                <div className="tier-item"><span>L2</span><span className="tier-pct">3%</span></div>
                <div className="tier-item"><span>L3</span><span className="tier-pct">2%</span></div>
                <div className="tier-item"><span>L4-L15</span><span className="tier-pct">1%</span></div>
              </div>
            </div>
            <div className="overview-card highlight">
              <h3>Direct Referral</h3>
              <div className="highlight-value">5%</div>
              <p>Instant commission on every referral investment</p>
            </div>
          </div>
        )}

        {activeTab === 'levels' && (
          <div className="levels-content">
            <div className="levels-list">
              {levelPercentages.map((percent, index) => (
                <div key={index}>
                  <div 
                    className={`level-item ${levelEligibility[index] ? 'eligible' : 'locked'} ${selectedLevel === index ? 'expanded' : ''}`}
                    onClick={() => handleLevelClick(index)}
                  >
                    <div className="level-info">
                      <span className="level-number">L{index + 1}</span>
                      <span className={`level-status ${levelEligibility[index] ? 'active' : ''}`}>
                        {levelEligibility[index] ? '✓' : '🔒'}
                      </span>
                    </div>
                    <div className="level-stats">
                      <div className="ls-item">
                        <span className="ls-label">Members</span>
                        <span className="ls-value">{levelCounts[index] || 0}</span>
                      </div>
                      <div className="ls-item">
                        <span className="ls-label">Volume</span>
                        <span className="ls-value">${(levelVolumes[index] || 0).toFixed(0)}</span>
                      </div>
                      <div className="ls-item">
                        <span className="ls-label">Income</span>
                        <span className="ls-value green">{percent}%</span>
                      </div>
                    </div>
                    <div className="level-arrow">{selectedLevel === index ? '▲' : '▼'}</div>
                  </div>
                  
                  {selectedLevel === index && (
                    <div className="level-users-panel">
                      {loadingUsers ? (
                        <div className="loading-users"><span className="spinner" /></div>
                      ) : levelUsers.length === 0 ? (
                        <div className="no-users">No users in this level</div>
                      ) : (
                        <div className="users-list">
                          {levelUsers.map((uid, i) => (
                            <div key={i} className="user-item">
                              <span className="user-icon">👤</span>
                              <span className="user-id">ID: {uid}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'direct' && (
          <div className="direct-content">
            {directReferrals.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">👥</div>
                <h3>No Direct Referrals Yet</h3>
                <p>Share your referral link to start building your team</p>
                <button className="share-btn" onClick={copyReferralLink}>Share Link</button>
              </div>
            ) : (
              <div className="direct-list">
                {directReferrals.map((refId, index) => (
                  <div key={refId} className="direct-item">
                    <div className="direct-avatar">👤</div>
                    <div className="direct-info">
                      <span className="direct-id">ID: {refId}</span>
                      <span className="direct-status">Active Member</span>
                    </div>
                    <div className="direct-check">✓</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Team;
